import React, { useState } from "react";
import api, { downloadAuthed } from "@/lib/api";
import { PageHeader, PrimaryButton } from "@/components/DataViews";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download, FileCode2, Table } from "lucide-react";
import { toast } from "sonner";

function useDownload() {
  return async (path, filename) => {
    try {
      await downloadAuthed(path, filename);
      toast.success(`${filename} downloaded`);
    } catch (e) { toast.error(e.message || "Failed"); }
  };
}

export default function TallySync() {
  const [range, setRange] = useState({ from: "", to: "" });
  const dl = useDownload();
  const q = () => {
    const p = new URLSearchParams();
    if (range.from) p.set("from_date", range.from);
    if (range.to) p.set("to_date", range.to);
    return p.toString() ? `?${p.toString()}` : "";
  };

  const [ledgerVendor, setLedgerVendor] = useState("");
  const [ledger, setLedger] = useState(null);
  const fetchLedger = async () => {
    if (!ledgerVendor.trim()) { toast.error("Enter a vendor name"); return; }
    try {
      const r = await api.get(`/vendor-ledger?vendor_name=${encodeURIComponent(ledgerVendor)}`);
      setLedger(r.data);
    } catch (e) { toast.error("Failed to load ledger"); }
  };

  return (
    <div data-testid="tally-sync-page">
      <PageHeader
        title="Tally Sync"
        subtitle="One-click reconciliation exports for Tally Prime. Bill-Wise references use the PO number, so advance & bill adjustment happens automatically inside Tally."
      />

      <Card className="p-5 mb-4">
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs uppercase font-semibold">From Date</Label>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="tally-from" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold">To Date</Label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="tally-to" />
          </div>
          <div className="text-[11px] text-muted-foreground">
            Leave blank to export everything. Dates are inclusive.
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <FileCode2 size={16} className="text-kabir-dark" />
            <div className="font-display font-semibold">Purchase Vouchers (XML)</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            All booked invoices as Tally <strong>Purchase</strong> vouchers with bill-wise <em>New Ref</em> pointing to the PO number.
          </p>
          <PrimaryButton onClick={() => dl(`/exports/tally/purchases.xml${q()}`, "tally-purchases.xml")} data-testid="dl-tally-purchases">
            <Download size={14} className="mr-2" />Download XML
          </PrimaryButton>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <FileCode2 size={16} className="text-kabir-dark" />
            <div className="font-display font-semibold">Payment Vouchers (XML)</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            All paid PVs — invoice payments as <em>Agst Ref</em> and vendor advances as <em>Advance</em>. Import once, Tally will auto-adjust on the next bill.
          </p>
          <PrimaryButton onClick={() => dl(`/exports/tally/payments.xml${q()}`, "tally-payments.xml")} data-testid="dl-tally-payments">
            <Download size={14} className="mr-2" />Download XML
          </PrimaryButton>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Table size={16} className="text-kabir-dark" />
            <div className="font-display font-semibold">Day Book (CSV)</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Human-readable ledger view — dates, vendors, bill refs, UTRs. Use for spot-checks &amp; audit.
          </p>
          <PrimaryButton onClick={() => dl(`/exports/tally/daybook.csv${q()}`, "tally-daybook.csv")} data-testid="dl-tally-daybook">
            <Download size={14} className="mr-2" />Download CSV
          </PrimaryButton>
        </Card>
      </div>

      {/* ---- Vendor Ledger Lookup ---- */}
      <Card className="p-5 mt-6">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Vendor Ledger (Live)</div>
        <div className="flex gap-2 mb-4">
          <Input value={ledgerVendor} onChange={(e) => setLedgerVendor(e.target.value)} placeholder="Type exact vendor name…" data-testid="ledger-vendor-input" />
          <Button variant="outline" onClick={fetchLedger} data-testid="ledger-lookup-btn">Look up</Button>
        </div>
        {ledger && (
          <>
            <div className="text-sm mb-2">
              Closing Balance: <span className={`font-mono font-bold ${ledger.closing_balance < 0 ? "text-emerald-700" : "text-amber-700"}`}>
                {ledger.closing_balance < 0 ? `-₹${Math.abs(ledger.closing_balance).toLocaleString("en-IN")}` : `₹${ledger.closing_balance.toLocaleString("en-IN")}`}
              </span>
              <span className="text-[11px] text-muted-foreground ml-2">
                ({ledger.closing_balance < 0 ? "Advance available with vendor" : "Payable to vendor"})
              </span>
            </div>
            <div className="border border-border rounded-md overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Bill Ref</th>
                    <th className="text-left px-3 py-2">Ref Type</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">Running Bal</th>
                    <th className="text-left px-3 py-2">Narration</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((e) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="px-3 py-1.5 text-xs">{(e.date || "").slice(0, 10)}</td>
                      <td className="px-3 py-1.5 text-xs">{e.voucher_type}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{e.bill_ref}</td>
                      <td className="px-3 py-1.5 text-xs">{e.ref_type}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${e.amount < 0 ? "text-emerald-700" : "text-amber-700"}`}>
                        {e.amount < 0 ? `-₹${Math.abs(e.amount).toLocaleString("en-IN")}` : `+₹${e.amount.toLocaleString("en-IN")}`}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">{e.running_balance?.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground truncate max-w-md">{e.narration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <Card className="p-5 mt-6 bg-secondary/40">
        <div className="text-[10px] uppercase tracking-widest font-semibold mb-2">How the Tally reconciliation works (foolproof)</div>
        <ol className="text-sm space-y-1.5 list-decimal ml-5">
          <li><strong>Bill-Wise Reference = PO Number</strong>. Every invoice, advance, and payment share the same Tally bill reference, so Tally auto-links them.</li>
          <li>When you create a PO with an <strong>Advance</strong>, an &quot;Advance PV&quot; is either paid outside system (record UTR here) or auto-generated for Accounts to disburse.</li>
          <li>The moment advance is marked <em>Paid</em>, it becomes a <code>Payment</code> voucher with <code>BILLTYPE=Advance</code> against the PO — matches your Tally bill-wise entry.</li>
          <li>When the vendor invoice is booked, the system deducts the outstanding advance automatically. Import <em>Purchase XML</em> → invoice enters Tally as <code>New Ref</code>.</li>
          <li>The final payment PV imports as <code>Agst Ref</code> and Tally auto-clears the invoice + adjusts the advance. Zero manual entries. Zero drift.</li>
        </ol>
      </Card>
    </div>
  );
}
